import Vue from "vue";
import VueI18n from "vue-i18n";

import ProductionOrderPdfComponent from "@/components/production-order/export/pdf/production-order-pdf-component.vue";

/**
 * @typedef {Object} AdjustmentResult
 * @property {Array} adjustments - List of calculated adjustments
 * @property {Array} fitAdjustments - List of fit adjustments
 * @property {string|null} error - Error message if any
 */

/**
 * Calculate adjustments and fit adjustments for an order
 * @param {Object} order - The order object
 * @param {Object} clothingSizeItemDefaults - Clothing size item defaults from store
 * @param {Function} translate - Translation function (this.$t)
 * @returns {Promise<AdjustmentResult>}
 */
export async function calculateOrderAdjustments(
  order,
  clothingSizeItemDefaults,
  translate
) {
  const adjustments = [];
  const fitAdjustments = [];

  if (!order || order.style.size_item_templates.length === 0) {
    return {
      adjustments,
      fitAdjustments,
      error: null
    };
  }

  const isRtwTailoring = order.tailoring === "RtW";

  for (const item of order.style.size_item_templates) {
    const measurement = order.queue_item.bodyprint_measurements.find(
      m => m.name === item.code
    );

    if (!measurement) {
      return {
        adjustments,
        fitAdjustments,
        error: translate(
          "production_order_detail.errors.incomplete_measurement_data"
        )
      };
    }

    const getBodyMeasurementValue = measurement => {
      return measurement.value.toFixed(1);
    };

    const fitAdjustment = order.fit_adjustments.find(
      f => f.size_item_code === item.code
    );

    if (!fitAdjustment) {
      return {
        adjustments,
        fitAdjustments,
        error: translate(
          "production_order_detail.errors.incomplete_fit_adjustment_data"
        )
      };
    }

    if (!order.style.fit_specification.specification_items) {
      return {
        adjustments,
        fitAdjustments,
        error: translate(
          "production_order_detail.errors.incomplete_fit_specification_data"
        )
      };
    }

    const fitSpecificationItem = order.style.fit_specification.specification_items[0].specs.find(
      m => m.code === item.code
    );

    if (!fitSpecificationItem) {
      return {
        adjustments,
        fitAdjustments,
        error: translate(
          "production_order_detail.errors.incomplete_fit_specification_data"
        )
      };
    }

    const getGarmentMeasurementValue = measurement => {
      const value = measurement.value + fitSpecificationItem.amount_of_space;

      const clothingSizeItem = clothingSizeItemDefaults.find(
        m => m.code === measurement.name
      );

      if (clothingSizeItem.type === "girth") {
        return (value / 2).toFixed(1);
      }
      return value.toFixed(1);
    };

    const dropAdjustment = order.style.drop_adjustments.find(
      m => m.code === item.code
    );

    const sizeItemLabel = translate(
      `production_order_detail.table.size_items.${item.code}`
    );

    let adjustment = {
      code: item.code,
      body_size_item: sizeItemLabel.replace("1/2", ""),
      size_item: sizeItemLabel,
      body_measurement: getBodyMeasurementValue(measurement),
      garment_measurement: getGarmentMeasurementValue(measurement),
      fit_specification: fitSpecificationItem.amount_of_space.toFixed(1),
      calculated_drop: dropAdjustment.value.toFixed(1)
    };

    if (isRtwTailoring) {
      const matchValue = order.match.values.find(m => m.code === item.code);

      adjustment = {
        ...adjustment,
        product_measurement: matchValue ? matchValue.base_size : 0
      };
    }

    adjustments.push(adjustment);
    fitAdjustments.push(fitAdjustment);
  }

  return {
    adjustments,
    fitAdjustments,
    error: null
  };
}

/**
 * @typedef {Object} ProductionOrderPdfProps
 * @property {Object} order - The order object
 * @property {boolean} isRtwTailoring - Whether it's RTW tailoring
 * @property {Array} adjustments - List of adjustments
 * @property {Array} fitAdjustments - List of fit adjustments
 * @property {string} imageBack - Back image URL
 * @property {Object} cropBack - Back crop data
 * @property {string} imageSide - Side image URL
 * @property {Object} cropSide - Side crop data
 */

/**
 * Export a component to PDF with i18n support
 * @param {string} selectedLanguage - The locale to use for PDF
 * @param {Object} i18nMessages - The i18n messages object
 * @param {Object} store - The Vuex store instance
 * @param {ProductionOrderPdfProps} propsData - Props to pass to the component
 */
export async function exportProductionOrderToPdf(
  selectedLanguage,
  i18nMessages,
  store,
  propsData
) {
  if (!i18nMessages) return;

  // Create separate i18n instance
  const pdfI18n = new VueI18n({
    locale: selectedLanguage,
    fallbackLocale: "en",
    messages: i18nMessages
  });

  const PdfExporterComponent = Vue.extend(ProductionOrderPdfComponent);

  // Create a separate Vue instance for the PDF generation
  const pdfInstance = new PdfExporterComponent({
    i18n: pdfI18n,
    store,
    propsData
  });

  pdfInstance.$mount();
  document.body.appendChild(pdfInstance.$el);

  try {
    await pdfInstance.generatePdf();
  } finally {
    // Clean up
    document.body.removeChild(pdfInstance.$el);
    pdfInstance.$destroy();
  }
}

/**
 * Export data to CSV
 * @param {string} selectedLanguage - The locale to use for CSV
 */
export function exportProductionOrderToCsv(selectedLanguage) {
  // TODO: Implement CSV export
  console.log("Exporting to CSV with language:", selectedLanguage);
}
